import { Check } from 'lucide-react';
import { useApplicationStore } from '../../store/application.store';
import { useNavigationStore } from '../../store/navigation.store';
import { useLogoStore } from '../../store/logo.store';
import { logger } from '@/features/help/utils/logger';
import { getApplicationBrandTheme } from '../../utils/brand-theme.utils';

// Purpose: Displays a sidebar with logo and form progress information
export const ApplicationSidebar = () => {
  const { structure, userShortId } = useApplicationStore();
  const { activePage, getVisiblePages } = useNavigationStore();
  const { logoUrl } = useLogoStore();

  // Safety check
  const visiblePages = getVisiblePages();

  if (!structure?.theme || visiblePages.length === 0) {
    return null;
  }

  const brandTheme = getApplicationBrandTheme(structure.theme, userShortId);
  const sidebarLogoUrl = brandTheme.logoOnDarkUrl || logoUrl;

  return (
    <div
      className="hidden md:flex md:flex-col h-screen fixed top-0 left-0 w-72 md:w-80 lg:w-72 xl:w-96 shadow-lg"
      style={{ backgroundColor: brandTheme.surfaceBackground }}
    >
      {/* Logo Section */}
      <div className="h-32 p-8 flex justify-center">
        {sidebarLogoUrl ? (
          <img
            src={sidebarLogoUrl}
            alt="Company Logo"
            className="h-12 w-auto object-contain"
            onError={() => {
              logger.error(
                'ApplicationSidebar',
                'Failed to display logo image despite preloading',
                { logoUrl: sidebarLogoUrl },
                'logo'
              );
            }}
            loading="eager"
            decoding="sync"
          />
        ) : (
          <div
            className="h-12 flex items-center justify-center"
            style={{ color: brandTheme.surfaceMutedForeground }}
          >
            {/* Empty placeholder */}
          </div>
        )}
      </div>

      {/* Page Steps */}
      <div className="flex-1 flex justify-center px-8 pt-6 pb-8">
        {/* Progress Steps */}
        <nav className="relative">
          {visiblePages.map((page, index) => {
            const isCompleted = index < activePage;
            const isCurrent = index === activePage;
            const _isUpcoming = index > activePage;

            return (
              <div key={index} className="relative flex items-center">
                {/* Vertical connecting line */}
                {index < visiblePages.length - 1 && (
                  <div
                    className="absolute left-4 top-8 w-0.5 h-12 -ml-px"
                    style={{
                      backgroundColor:
                        isCompleted || isCurrent
                          ? brandTheme.progressConnectorActive
                          : brandTheme.progressConnectorInactive,
                    }}
                  />
                )}

                {/* Step circle and content */}
                <div className="relative flex items-center pb-12">
                  {/* Circle */}
                  <div
                    className="h-8 w-8 rounded-full flex items-center justify-center relative z-10 transition-colors"
                    style={{
                      backgroundColor:
                        isCompleted || isCurrent
                          ? brandTheme.progressActiveBackground
                          : brandTheme.progressInactiveBackground,
                      color:
                        isCompleted || isCurrent
                          ? brandTheme.progressActiveForeground
                          : brandTheme.progressInactiveForeground,
                    }}
                  >
                    {isCompleted ? (
                      <Check className="h-4 w-4 stroke-2" />
                    ) : (
                      <span className="text-xs font-semibold">{index + 1}</span>
                    )}
                  </div>

                  {/* Step label */}
                  <span
                    className="ml-4 text-sm font-medium"
                    style={{
                      color: brandTheme.surfaceForeground,
                      opacity: isCurrent ? 1 : isCompleted ? 0.9 : 0.7,
                    }}
                  >
                    {page.title || `Page ${index + 1}`}
                  </span>
                </div>
              </div>
            );
          })}
        </nav>
      </div>
    </div>
  );
};
